import React, { useState } from 'react';
import { Plus, CheckSquare, Square, Trash2, X, Pause, Play } from 'lucide-react';
import { relativeTime, sessionLabel } from './chatHelpers';

const shortTokens = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : String(n || 0));

export default function SessionList({ sessions, activeId, onSelect, onNew, onDelete, onPause, onPlay }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  // Running chats first, then newest; the search box only appears once the list is long enough to need it.
  const sorted = sessions
    .filter((s) => !needle || sessionLabel(s).toLowerCase().includes(needle))
    .sort((a, b) => (b.status === 'running') - (a.status === 'running')
      || new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  const [selecting, setSelecting] = useState(false);
  const [ticked, setTicked] = useState(() => new Set());

  const stop = () => { setSelecting(false); setTicked(new Set()); };
  const toggle = (id) => setTicked((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allTicked = sorted.length > 0 && ticked.size === sorted.length;
  const remove = async () => {
    if (!ticked.size || !window.confirm(`Delete ${ticked.size} chat${ticked.size > 1 ? 's' : ''}? This cannot be undone.`)) return;
    if (await onDelete([...ticked])) stop();
  };

  return (
    <nav className="cc-list" aria-label="Chats">
      {selecting ? (
        <div className="cc-list-bar">
          <button type="button" className="cc-list-new" onClick={() => setTicked(allTicked ? new Set() : new Set(sorted.map((s) => s._id)))}>
            {allTicked ? <CheckSquare size={16} /> : <Square size={16} />} All
          </button>
          <button type="button" className="cc-list-new cc-list-danger" disabled={!ticked.size} onClick={remove}><Trash2 size={16} /> Delete ({ticked.size})</button>
          <button type="button" className="cc-icon-btn" onClick={stop} aria-label="Cancel selection"><X size={18} /></button>
        </div>
      ) : (
        <div className="cc-list-bar">
          <button type="button" className="cc-list-new" onClick={onNew}><Plus size={16} /> New session</button>
          {sorted.length > 0 && <button type="button" className="cc-list-new" onClick={() => setSelecting(true)}><CheckSquare size={16} /> Select</button>}
        </div>
      )}
      {sessions.length > 6 && (
        <input className="cc-list-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chats" aria-label="Search chats" />
      )}
      {needle && sorted.length === 0 && <div className="cc-list-time" style={{ padding: '8px 12px' }}>No chats match.</div>}
      <div className="cc-list-items">
        {sorted.map((s) => (
          <div key={s._id} className="cc-list-row">
            {!selecting && s.status === 'running' && (
              <button type="button" className="cc-icon-btn cc-pp" onClick={() => onPause(s._id)} aria-label="Pause: stop using tokens" title="Pause: stop using tokens"><Pause size={16} /></button>
            )}
            {!selecting && s.status !== 'running' && s.paused && (
              <button type="button" className="cc-icon-btn cc-pp" onClick={() => onPlay(s._id)} aria-label="Play: continue this chat" title="Play: continue this chat"><Play size={16} /></button>
            )}
            <button
              type="button" onClick={() => (selecting ? toggle(s._id) : onSelect(s._id))}
              className={`cc-list-item${s._id === activeId && !selecting ? ' cc-active' : ''}`}
              aria-current={s._id === activeId && !selecting ? 'true' : undefined}
            >
              {selecting && (ticked.has(s._id) ? <CheckSquare size={18} className="cc-tick" /> : <Square size={18} className="cc-tick" />)}
              <span className="cc-list-text">
                <span className="cc-list-title">{sessionLabel(s)}</span>
                <span className="cc-list-time">
                  {s.status === 'running' && <span className="cc-live">● Using tokens</span>}
                  {s.status !== 'running' && s.paused && <span className="cc-paused">Paused</span>}
                  {relativeTime(s.updatedAt || s.createdAt)}
                  {s.tokensUsed > 0 && ` · ${shortTokens(s.tokensUsed)} used · ${shortTokens(s.context)} context`}
                </span>
              </span>
            </button>
          </div>
        ))}
      </div>
    </nav>
  );
}
